import {
  $applyNodeReplacement,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type EditorConfig,
  type LexicalNode,
  type NodeKey,
  type SerializedTextNode,
  type Spread,
  TextNode,
} from 'lexical';

interface MentionData {
  id: string;
  name: string;
  email: string;
}

export type SerializedMentionNode = Spread<
  {
    mentionId: string;
    mentionName: string;
    mentionEmail: string;
  },
  SerializedTextNode
>;

function $convertMentionElement(
  domNode: HTMLElement,
): DOMConversionOutput | null {
  const textContent = domNode.textContent;

  if (textContent !== null) {
    const node = $createMentionNode({
      id: domNode.getAttribute('data-id') || '',
      name: textContent,
      email: domNode.getAttribute('data-email') || ''
    });
    return {
      node,
    };
  }

  return null;
}

const mentionStyle = `
  color: #58a6ff;
`;

export class MentionNode extends TextNode {
  __mention: MentionData;

  static getType(): string {
    return 'mention';
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(node.__mention, node.__text, node.__key);
  }

  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    const node = $createMentionNode({
      id: serializedNode.mentionId,
      name: serializedNode.mentionName,
      email: serializedNode.mentionEmail
    });
    node.setTextContent(serializedNode.text);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  constructor(mention: MentionData, text?: string, key?: NodeKey) {
    // Display only the name, store the full data in __mention
    const displayText = mention.name;
    super(text ?? displayText, key);
    this.__mention = mention;
  }

  exportJSON(): SerializedMentionNode {
    return {
      ...super.exportJSON(),
      mentionId: this.__mention.id,
      mentionName: this.__mention.name,
      mentionEmail: this.__mention.email,
      type: 'mention',
      version: 1,
    };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.style.cssText = mentionStyle;
    dom.className = 'mention';
    dom.setAttribute('data-id', this.__mention.id);
    dom.setAttribute('data-name', this.__mention.name);
    dom.setAttribute('data-email', this.__mention.email);
    dom.spellcheck = false;
    return dom;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('span');
    element.setAttribute('data-lexical-mention', 'true');
    element.setAttribute('data-email', this.__mention.email);
    element.setAttribute('data-id', this.__mention.id);
    element.setAttribute('data-name', this.__mention.name);
    element.textContent = this.__text;
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-mention')) {
          return null;
        }
        return {
          conversion: $convertMentionElement,
          priority: 1,
        };
      },
    };
  }

  isTextEntity(): true {
    return true;
  }

  canInsertTextBefore(): boolean {
    return false;
  }

  canInsertTextAfter(): boolean {
    return false;
  }
}

export function $createMentionNode(mention: MentionData): MentionNode {
  const mentionNode = new MentionNode(mention);
  mentionNode.setMode('segmented').toggleDirectionless();
  return $applyNodeReplacement(mentionNode);
}

export function $isMentionNode(
  node: LexicalNode | null | undefined,
): node is MentionNode {
  return node instanceof MentionNode;
}